import { useQueryClient } from '@tanstack/react-query';

import { ImportEntitiesModal } from '@/modules/onboarding/common/import-entities-modal/import-entities-modal';
import {
  invalidateOnboardingClientQueries,
  invalidateOnboardingProjectValidationReport,
  removeOnboardingClientListCache,
} from '@/modules/onboarding/common/utils/onboarding-query-invalidation';

import { importProjectsFromCsv } from '../../api/projects';
import { getErrorColumns } from './error-columns';

export function ImportOnboardingProjectsModal({
  onSuccess,
}: {
  onSuccess?: (addedCount: number) => void;
}) {
  const queryClient = useQueryClient();
  return (
    <ImportEntitiesModal
      entityRelatedProps={{
        templateLinkHref: '/templates/projects-template.csv',
        templateLinkLabel: 'projects-template.csv',
        importEntitiesFromCsv: async (file: File) => {
          const result = await importProjectsFromCsv(file);
          return result.importErrors ?? [];
        },
        onSuccess: async addedCount => {
          await invalidateOnboardingProjectValidationReport(queryClient);
          await invalidateOnboardingClientQueries(queryClient);

          // Project import can add new clients — remove the client list cache so the
          // clients step always fetches fresh data on next mount rather than showing
          // stale cached entries.
          removeOnboardingClientListCache(queryClient);
          onSuccess?.(addedCount);
        },
        res: {
          addedSuccessfully: 'authenticated.onboarding.projects:importModal.addedSuccessfully',
          title: 'authenticated.onboarding.projects:importModal.title',
          failedImport: 'authenticated.onboarding.projects:importModal.failedImport',
          successImport: 'authenticated.onboarding.projects:importModal.successImport',
          step1Description: 'authenticated.onboarding.projects:importModal.step1Description',
          step2Description: 'authenticated.onboarding.projects:importModal.step2Description',
          backToImport: 'authenticated.onboarding.projects:importModal.backToImport',
        },
      }}
      getErrorEntitiesColumns={getErrorColumns}
      importStringResource={'authenticated.onboarding.projects:import'}
    />
  );
}
